import { Flex, IconButton, Image, Text } from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { FaTrash } from 'react-icons/fa';

import QuantityPicker from '@features/marketplace/catalog/QuantityPicker';
import { allProducts, getAttributeDescription } from 'helpers/fakeData';
import { CartItem, getCartItemKey } from 'stores/marketPlaceStore';
import { useStores } from 'stores/rootStore';

interface CartItemDetailsProps {
  item: CartItem;
  onDelete: (item: CartItem) => void;
  onQuantityChange: (item: CartItem, quantity: number) => void;
}

const CartItemDetails = ({ item, onDelete, onQuantityChange }: CartItemDetailsProps) => {
  const product = allProducts.find((p) => p.id === item.id);

  const handleQuantityChange = useCallback(
    (quantity: number) => {
      onQuantityChange(item, quantity);
    },
    [item, onQuantityChange],
  );

  const handleDelete = useCallback(() => {
    onDelete(item);
  }, [item, onDelete]);

  if (product == null) {
    return null;
  }

  const attributeDescriptions = product.attributeIds?.map((attributeId) =>
    getAttributeDescription(attributeId),
  );

  return (
    <Flex
      gap={6}
      p={4}
      borderWidth={1}
      borderColor="border.light"
      borderRadius={4}
      w="100%"
      h="fit-content"
    >
      <Image w="100px" src={product.image} alt={product.name} fit="contain" />
      <Flex flexDirection="column" gap={3} w="100%">
        <Text>{product.name}</Text>
        <Text textStyle="lg" fontWeight="medium" letterSpacing="tight">
          {product.currency}
          {product.price}
        </Text>
        <Flex gap={2} color="text.secondary" flex={1}>
          {attributeDescriptions?.map((description) => (
            <Text key={description}>{description}</Text>
          ))}
        </Flex>
        <Flex alignItems="center" gap={2} w="100%" justifyContent="space-between">
          <QuantityPicker quantity={item.quantity} onChange={handleQuantityChange} />
          <IconButton size="2xs" variant="ghost" onClick={handleDelete}>
            <FaTrash />
          </IconButton>
        </Flex>
      </Flex>
    </Flex>
  );
};

const CartList = () => {
  const { marketPlaceStore } = useStores();
  const cartItems = marketPlaceStore.cart;

  const handleDelete = useCallback(
    (item: CartItem) => {
      marketPlaceStore.removeFromCart(item);
    },
    [marketPlaceStore],
  );

  const handleQuantityChange = useCallback(
    (item: CartItem, quantity: number) => {
      marketPlaceStore.addToCart({ ...item, quantity });
    },
    [marketPlaceStore],
  );

  if (cartItems.length === 0) {
    return (
      <Flex h="100%" w="100%" justifyContent="center" alignItems="center" color="text.secondary">
        No items in cart
      </Flex>
    );
  }

  return (
    <Flex w="100%" h="100%" flexDirection="column" gap={4}>
      {cartItems.map((item) => (
        <CartItemDetails
          key={getCartItemKey(item)}
          item={item}
          onDelete={handleDelete}
          onQuantityChange={handleQuantityChange}
        />
      ))}
    </Flex>
  );
};

export default observer(CartList);
